/**
 * A RÉGUA "SALVO HÁ 5 S" — T8.28, e o T20 (12/10/2026) que a tirou de dentro do editor.
 *
 * Ela precisa andar sozinha a cada segundo; senão "Salvo há 5 s" fica congelado e parece atual.
 * Até 12/10 o relógio que a fazia andar era um `setState` do EDITOR INTEIRO: a cada segundo a tela
 * toda redesenhava, e cada item da folha era REMONTADO (os itens são componentes criados dentro do
 * `renderPage`, então um redesenho do editor é uma montagem nova de cada um).
 *
 * Medido no ar em 12/10: o balão selecionado foi remontado 21 vezes em 21 segundos, sem ninguém
 * tocar em nada. Daí os três erros que o operador colou —
 *
 *   · `offsetWidth` de `null` e "Unable to find element in cloned iframe": o texto de cada balão é
 *     rasterizado por `html2canvas`, e o balão saía da tela no meio da rasterização;
 *   · `scaleX` de `undefined` → "Alguma coisa deu errado": o redimensionamento que durasse mais
 *     que a janela de um segundo perdia a moldura no meio do gesto.
 *
 * Aqui o relógio redesenha SÓ a régua. O editor não sabe que o segundo passou.
 */
import { useEffect, useState } from "react";

export type EstadoDoSalvamento =
  | { estado: "nunca" }
  | { estado: "salvando" }
  | { estado: "salvo"; quando: number }
  | { estado: "falhou" };

/** A frase da régua. Pura: o relógio entra como argumento, para o teste não depender da hora. */
export function fraseDoSalvamento(salvamento: EstadoDoSalvamento, agora: number): string {
  if (salvamento.estado === "nunca") return "Ainda não salvo";
  if (salvamento.estado === "salvando") return "Salvando…";
  if (salvamento.estado === "falhou") return "Não deu para salvar";
  const s = Math.round((agora - salvamento.quando) / 1000);
  if (s < 5) return "Salvo agora";
  if (s < 60) return `Salvo há ${s} s`;
  const m = Math.round(s / 60);
  return `Salvo há ${m} min`;
}

export default function ReguaDoSalvamento({ salvamento }: { salvamento: EstadoDoSalvamento }) {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    setAgora(Date.now());
    if (salvamento.estado !== "salvo") return;
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [salvamento]);

  const frase = fraseDoSalvamento(salvamento, agora);
  // `aria-live="polite"` para quem usa leitor de tela ouvir a mudança sem ir procurar; `title`
  // com a frase inteira porque a régua é estreita.
  return (
    <p className={`regua-do-salvamento estado-${salvamento.estado}`} aria-live="polite" title={frase}>
      {frase}
    </p>
  );
}
